import type { CSSProperties } from 'react';

import {
  defaultThemeTokens,
  type ThemeContextValue,
} from '@course-authoring/shared-types';

type EditorTokens = ThemeContextValue['tokens'];
const tokens = defaultThemeTokens;

export interface StylizedSidebarCalloutEditorStyles {
  container: CSSProperties;
  fieldContainer: CSSProperties;
  titleLevelControls: CSSProperties;
  titleLevelLabel: CSSProperties;
  titleLevelButtonGroup: CSSProperties;
  titleLevelButton: CSSProperties;
  titleLevelButtonActive: CSSProperties;
}

export const stylizedSidebarCalloutEditorInnerStyles = `
  .ssc-editor p {
    margin: 0;
  }
  .ssc-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
  .ssc-editor p.is-editor-empty:first-child::before {
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

export const stylizedSidebarCalloutToolbarStyles: Record<string, CSSProperties> = {
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${tokens.borders.default}`,
  },
  toolbarButton: {
    minWidth: '32px',
    height: '32px',
    padding: '0 8px',
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.text.muted,
    backgroundColor: tokens.backgrounds.app,
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: tokens.radius.sm,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: tokens.typography.fontFamily,
  },
  toolbarButtonActive: {
    backgroundColor: tokens.ui.selectionBorder,
    color: tokens.text.onInverse,
  },
};

export function createStylizedSidebarCalloutEditorStyles(
  tokens: EditorTokens,
  isFocused: boolean
): StylizedSidebarCalloutEditorStyles {
  return {
    container: {
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: isFocused ? tokens.ui.selectionBorder : 'transparent',
      borderRadius: tokens.radius.md,
      transition: `border-color ${tokens.ui.transitionDefault}`,
      boxSizing: 'border-box',
      backgroundColor: 'transparent',
      padding: 0,
    },
    fieldContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    titleLevelControls: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.md,
    },
    titleLevelLabel: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
    },
    titleLevelButtonGroup: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.xs,
      padding: tokens.spacing.xs,
      borderRadius: tokens.radius.md,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      boxShadow: tokens.shadow.sm,
    },
    titleLevelButton: {
      minWidth: '36px',
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      border: 'none',
      borderRadius: tokens.radius.sm,
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
    },
    titleLevelButtonActive: {
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.primary,
      boxShadow: `inset 0 0 0 1px ${tokens.ui.selectionBorder}`,
    },
  };
}
